import React, { createContext, useContext, useReducer, useCallback, useMemo, useEffect } from 'react';
import { EditorState, EditorAction, FormDefinition, Field, Group } from '../types';
import { arrayMove } from '@dnd-kit/sortable';

const defaultForm: FormDefinition = {
  id: 'form-1',
  name: 'New Form',
  version: '1.0',
  fields: [],
  groups: [],
  modifiersScript: ''
};

const createInitialState = (initialForm?: FormDefinition): EditorState => {
    const form = initialForm || defaultForm;
    return {
        form: form,
        selectedElementId: null,
        selectedElementType: null,
        isPreviewMode: false,
        history: [form],
        historyIndex: 0,
    };
};

const pushHistory = (state: EditorState, newForm: FormDefinition): EditorState => {
  const newHistory = state.history.slice(0, state.historyIndex + 1);
  newHistory.push(newForm);
  // Limit history size to 50
  if (newHistory.length > 50) newHistory.shift();
  
  return {
    ...state,
    form: newForm,
    history: newHistory,
    historyIndex: newHistory.length - 1,
  };
};

const editorReducer = (state: EditorState, action: EditorAction): EditorState => {
  switch (action.type) {
    case 'SET_FORM_NAME':
      return pushHistory(state, { ...state.form, name: action.payload });

    case 'SET_FORM_MODIFIERS_SCRIPT':
      return pushHistory(state, { ...state.form, modifiersScript: action.payload });
    
    case 'ADD_FIELD': {
      const newFields = [...state.form.fields];
      const fieldToAdd = { ...action.payload.field, groupId: action.payload.groupId };
      
      if (typeof action.payload.index === 'number') {
        newFields.splice(action.payload.index, 0, fieldToAdd);
      } else {
        newFields.push(fieldToAdd);
      }
      return {
        ...pushHistory(state, { ...state.form, fields: newFields }),
        selectedElementId: fieldToAdd.id,
        selectedElementType: 'field',
      };
    }

    case 'UPDATE_FIELD': {
      const { id, updates } = action.payload;
      const newFields = state.form.fields.map((f) =>
        f.id === id ? { ...f, ...updates } : f
      );
      
      const nextState = pushHistory(state, { ...state.form, fields: newFields });
      
      if (updates.id && state.selectedElementId === id && state.selectedElementType === 'field') {
          return {
              ...nextState,
              selectedElementId: updates.id
          };
      }
      return nextState;
    }

    case 'DELETE_FIELD': {
      const newFields = state.form.fields.filter((f) => f.id !== action.payload);
      const nextSelectedId = state.selectedElementId === action.payload ? null : state.selectedElementId;
      const nextSelectedType = state.selectedElementId === action.payload ? null : state.selectedElementType;
      return {
        ...pushHistory(state, { ...state.form, fields: newFields }),
        selectedElementId: nextSelectedId,
        selectedElementType: nextSelectedType,
      };
    }

    case 'DUPLICATE_FIELD': {
      const fieldIndex = state.form.fields.findIndex(f => f.id === action.payload);
      if (fieldIndex === -1) return state;
      
      const originalField = state.form.fields[fieldIndex];
      let counter = 1;
      let newId = `${originalField.id}_copy${counter}`;
      while (state.form.fields.some(f => f.id === newId)) {
          counter++;
          newId = `${originalField.id}_copy${counter}`;
      }

      const newField: Field = {
          ...originalField,
          id: newId,
          label: `${originalField.label} (Copy)`
      };

      const newFields = [...state.form.fields];
      newFields.splice(fieldIndex + 1, 0, newField);

      return {
          ...pushHistory(state, { ...state.form, fields: newFields }),
          selectedElementId: newId,
          selectedElementType: 'field',
      };
    }

    case 'SELECT_ELEMENT':
      return { 
          ...state, 
          selectedElementId: action.payload.id,
          selectedElementType: action.payload.type
      };

    case 'MOVE_FIELD': {
      const { fieldId, targetGroupId, newIndex } = action.payload;
      const currentFields = [...state.form.fields];
      const fieldIndex = currentFields.findIndex(f => f.id === fieldId);
      
      if (fieldIndex === -1) return state;

      const field = { ...currentFields[fieldIndex], groupId: targetGroupId };
      
      currentFields.splice(fieldIndex, 1);
      
      const fieldsInTargetGroup = state.form.fields.filter(f => f.groupId === targetGroupId && f.id !== fieldId);
      
      let insertAtIndex = state.form.fields.length; 
      
      if (fieldsInTargetGroup.length > 0) {
          if (newIndex >= fieldsInTargetGroup.length) {
               const lastField = fieldsInTargetGroup[fieldsInTargetGroup.length - 1];
               insertAtIndex = currentFields.findIndex(f => f.id === lastField.id) + 1;
          } else {
               const targetField = fieldsInTargetGroup[newIndex];
               insertAtIndex = currentFields.findIndex(f => f.id === targetField.id);
          }
      } else {
          insertAtIndex = currentFields.length; 
      }
      
      currentFields.splice(insertAtIndex, 0, field);

      return pushHistory(state, { ...state.form, fields: currentFields });
    }

    case 'ADD_GROUP': {
        return {
            ...pushHistory(state, { ...state.form, groups: [...state.form.groups, action.payload] }),
            selectedElementId: action.payload.id,
            selectedElementType: 'group'
        };
    }

    case 'UPDATE_GROUP': {
        const { id, updates } = action.payload;
        const newGroups = state.form.groups.map(g => 
            g.id === id ? { ...g, ...updates } : g
        );
        const nextState = pushHistory(state, { ...state.form, groups: newGroups });

        if (updates.id && state.selectedElementId === id && state.selectedElementType === 'group') {
            return {
                ...nextState,
                selectedElementId: updates.id
            };
        }
        return nextState;
    }

    case 'DELETE_GROUP': {
        const newGroups = state.form.groups.filter(g => g.id !== action.payload);
        const newFields = state.form.fields.map(f => 
            f.groupId === action.payload ? { ...f, groupId: undefined } : f
        );
        
        return {
            ...pushHistory(state, { ...state.form, groups: newGroups, fields: newFields }),
            selectedElementId: null,
            selectedElementType: null
        };
    }
    
    case 'REORDER_GROUPS': {
        const { activeId, overId } = action.payload;
        const oldIndex = state.form.groups.findIndex(g => g.id === activeId);
        const newIndex = state.form.groups.findIndex(g => g.id === overId);
        
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return state;

        const newGroups = arrayMove(state.form.groups, oldIndex, newIndex);
        return pushHistory(state, { ...state.form, groups: newGroups });
    }

    case 'TOGGLE_PREVIEW':
      return { ...state, isPreviewMode: action.payload };

    case 'UNDO': {
      if (state.historyIndex <= 0) return state;
      const prevIndex = state.historyIndex - 1;
      return {
        ...state,
        form: state.history[prevIndex],
        historyIndex: prevIndex,
        selectedElementId: null,
        selectedElementType: null,
      };
    }

    case 'REDO': {
      if (state.historyIndex >= state.history.length - 1) return state;
      const nextIndex = state.historyIndex + 1;
      return {
        ...state,
        form: state.history[nextIndex],
        historyIndex: nextIndex,
        selectedElementId: null,
        selectedElementType: null,
      };
    }
    
    case 'LOAD_FORM':
        const loadedForm = {
            ...action.payload,
            groups: action.payload.groups || [],
            modifiersScript: action.payload.modifiersScript || ''
        };
        return {
            ...createInitialState(loadedForm)
        };

    default:
      return state;
  }
};

const EditorContext = createContext<{
  state: EditorState;
  dispatch: React.Dispatch<EditorAction>;
  generateId: (prefix: string) => string;
  onSave?: (form: FormDefinition) => void;
  onBack?: () => void;
} | null>(null);

interface EditorProviderProps {
    children: React.ReactNode;
    initialForm?: FormDefinition;
    onSave?: (form: FormDefinition) => void;
    onBack?: () => void;
}

export const EditorProvider: React.FC<EditorProviderProps> = ({ children, initialForm, onSave, onBack }) => {
  const [state, dispatch] = useReducer(editorReducer, initialForm, createInitialState);

  // If initialForm reference changes deeply (or we just want to support re-initialization), we can watch it.
  // However, usually "initial" props are only used for mounting.
  // We'll leave it as uncontrolled with initial value for now. 
  // If the parent wants to force update, they can change the 'key' of the EditorProvider.

  const generateId = useCallback((prefix: string) => {
    let counter = 1;
    let newId = `${prefix}${counter}`;
    while (
        state.form.fields.some((f) => f.id === newId) || 
        state.form.groups.some((g) => g.id === newId)
    ) {
      counter++;
      newId = `${prefix}${counter}`;
    }
    return newId;
  }, [state.form.fields, state.form.groups]);

  const value = useMemo(() => ({ 
      state, 
      dispatch, 
      generateId,
      onSave,
      onBack
  }), [state, generateId, onSave, onBack]);

  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  );
};

export const useEditor = () => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditor must be used within an EditorProvider');
  }
  return context;
};